import React, { useEffect, useState } from "react";
import { Receipt, Wallet, Plus, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, Spinner, PageHeader, Empty } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CAT_TINT = { ingredient: "text-sky-400", packaging: "text-violet-400", wastage: "text-rose-400", manual: "text-amber-400", payment: "text-emerald-400" };

export default function Billing() {
  const { user } = useAuth();
  const isFinance = ["admin", "finance"].includes(user.role);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [ledger, setLedger] = useState(null);
  const [dlg, setDlg] = useState(null); // 'pay' | 'charge'
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => { setClients(r.data); setClientId(r.data[0]?.id || ""); });
  }, []);
  const load = (cid) => api.get(`/billing/ledger${cid ? `?client_id=${cid}` : ""}`).then((r) => setLedger(r.data));
  useEffect(() => { if (clientId) load(clientId); }, [clientId]);

  const client = clients.find((c) => c.id === clientId);
  const bal = client?.balance;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (dlg === "pay") await api.post("/payments", { client_id: clientId, amount: Number(form.amount), method: form.method, reference: form.reference, notes: form.notes });
      if (dlg === "charge") await api.post("/billing/manual", { client_id: clientId, amount: Number(form.amount), description: form.description, kind: form.kind });
      toast.success("Recorded");
      setDlg(null); load(clientId);
      const r = await api.get("/clients"); setClients(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Client Billing" subtitle="Live ledger of charges and payments at client-facing rates">
        {isFinance && clientId && (
          <>
            <Button variant="outline" onClick={() => { setForm({ amount: 0, description: "", kind: "charge" }); setDlg("charge"); }} data-testid="manual-charge-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Charge / Credit</Button>
            <Button onClick={() => { setForm({ amount: 0, method: "Bank Transfer", reference: "", notes: "" }); setDlg("pay"); }} data-testid="record-payment-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold"><DollarSign className="h-4 w-4" /> Record Payment</Button>
          </>
        )}
      </PageHeader>

      {isFinance && (
        <div className="max-w-xs mb-6">
          <SelectBox value={clientId} onChange={setClientId} options={clients.map((c) => ({ value: c.id, label: c.name }))} testid="billing-client-select" />
        </div>
      )}

      {bal && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[["Opening Balance", bal.opening_balance, "text-slate-300"], ["Unbilled Charges", bal.unbilled_charges, "text-amber-300"], ["Payments Received", bal.payments_received, "text-emerald-400"], ["Current Balance", bal.current_balance, "text-white"]].map(([l, v, t]) => (
            <div key={l} className="card-surface p-5" data-testid={`bal-${l.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-2">{l}</p>
              <p className={`text-xl sm:text-2xl font-extrabold font-mono ${t}`}>{money(v)}</p>
            </div>
          ))}
        </div>
      )}

      {!ledger ? <Spinner /> : ledger.length === 0 ? <Empty icon={Receipt} title="No ledger entries yet" hint="Charges appear automatically when orders complete." /> : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent border-border">
              <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
              <TableHead className="text-right">Charge</TableHead><TableHead className="text-right">Payment</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ledger.map((e) => (
                <TableRow key={e.id} className="border-border" data-testid={`ledger-row-${e.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{e.date}</TableCell>
                  <TableCell className={`capitalize font-medium ${CAT_TINT[e.category] || "text-slate-300"}`}>{e.category}</TableCell>
                  <TableCell className="text-slate-200">{e.description}</TableCell>
                  <TableCell className="text-right font-mono text-amber-300">{e.kind === "charge" ? money(e.amount) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-400">{e.kind === "payment" ? money(e.amount) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>{dlg === "pay" ? "Record Payment" : "Manual Charge / Credit"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FieldW label="Amount (AED)"><Input type="number" data-testid="billing-amount" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></FieldW>
            {dlg === "pay" && <>
              <FieldW label="Method"><SelectBox value={form.method} onChange={(v) => set("method", v)} options={["Bank Transfer", "Cash", "Card", "Cheque"]} /></FieldW>
              <FieldW label="Reference"><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} /></FieldW>
              <FieldW label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FieldW>
            </>}
            {dlg === "charge" && <>
              <FieldW label="Type"><SelectBox value={form.kind} onChange={(v) => set("kind", v)} options={[{ value: "charge", label: "Charge" }, { value: "credit", label: "Credit" }]} /></FieldW>
              <FieldW label="Description"><Input data-testid="charge-desc" value={form.description} onChange={(e) => set("description", e.target.value)} /></FieldW>
            </>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-billing-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
